
import googleAnalytics from "./ga4";
import APIJSON from '../../config/api.json';
import { SectionSystemCode, ValidationError } from "../../common/types";
import { TPortalMode } from "../../pages/sections/utils";

export const EVENTS = {
    FORM_START: 'form_start',
    FORM_SUBMIT_ATTEMPT: 'form_submit_attempt',
    FORM_SUBMIT_SUCCESS: 'form_submit_success',
    FORM_FIELD_ERROR: 'form_field_error',
} as const;

type EventName = typeof EVENTS[keyof typeof EVENTS];

const MEASUREMENT_ID_MAP: Record<TPortalMode, string> = {
    EZReferral: APIJSON.api.ga4EasyReferralMeasurementId,
    EZAuth: APIJSON.api.ga4PhysicianServicesMeasurementId,
};

const FORM_NAME_MAP: Record<TPortalMode, string> = {
    EZReferral: 'easy_referral',
    EZAuth: 'physician_services',
};

class AnalyticsService {
    private readonly formName: string;
    private readonly measurementId: string;
    private formStartTracked = false;
    private formFieldErrorsTracked = new Map<string, string>();

    constructor(mode: TPortalMode) {
        this.formName = FORM_NAME_MAP[mode];
        this.measurementId = MEASUREMENT_ID_MAP[mode];

        googleAnalytics.initGA(this.measurementId);
    }

    private trackEvent(name: EventName, props?: Record<string, any>) {
        googleAnalytics.event(name, { form_name: this.formName, ...props });
    }

    trackFormStart() {
        if (this.formStartTracked) {
            return;
        }

        this.formStartTracked = true;
        this.trackEvent(EVENTS.FORM_START);
    }

    trackFormSubmitAttempt() {
        this.trackEvent(EVENTS.FORM_SUBMIT_ATTEMPT);
    }

    trackFormSubmitSuccess() {
        this.trackEvent(EVENTS.FORM_SUBMIT_SUCCESS, {
            submission_status: 'success',
        });
    }

    trackSectionErrors(sectionSystemCode: SectionSystemCode | string, errors: ValidationError[]) {
        errors
            .filter(e => (e.errors || []).length > 0)
            .forEach(e => {
                const key = `${sectionSystemCode}_${e.fieldName}`;
                const errorValue = e.errors!.join(';');

                // Deduplicate tracking of the same error on repeated validations
                if (this.formFieldErrorsTracked.get(key) === errorValue) {
                    return;
                }

                this.formFieldErrorsTracked.set(key, errorValue);
                this.trackEvent(EVENTS.FORM_FIELD_ERROR, {
                    section_system_code: sectionSystemCode,
                    field_name: e.fieldName,
                    field_label: e.fieldLabel,
                    field_error: errorValue,
                });
            });
    }
}

export let analyticsService: AnalyticsService;

export function initAnalytics(mode: TPortalMode) {
    analyticsService = new AnalyticsService(mode);
}